import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import {
  COUNTRIES,
  CustomPaginator,
  GenericTable,
  GenericTableProps,
  UserGroupCreateModal,
  UserGroupCreateModalTranslations,
} from '@cdaas/reusable-components-daas';
import { UserGroupRequest, UserGroupWithUsersResponse, UserRole } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { Button, Div } from '@gaiads/telia-react-component-library';

import { assetAdminOrganizationClient, assetAdminUserGroupClient } from '../../api-client/asset-admin-client';
import { routes, withCustomerOrgFormPath } from '../App/routes';

const pageSize = 10;

enum SortableColumns {
  Name,
}

const UserGroups = () => {
  const { t } = useTranslation('userGroups');
  const history = useHistory();

  const [loading, setLoading] = useState<GenericTableProps<UserGroupWithUsersResponse>['loading']>('short');
  const [apiError, setApiError] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [userGroups, setUserGroups] = useState<UserGroupWithUsersResponse[]>([]);
  const [userGroupsPage, setUserGroupsPage] = useState<UserGroupWithUsersResponse[] | undefined>();

  const [currentSort, setCurrentSort] = useState<SortableColumns>();

  const organizationId = useParams<{ id: string }>().id;

  const handlePageChange = (pageItems: UserGroupWithUsersResponse[]) => {
    setUserGroupsPage(pageItems);
  };

  const fetchUserGroups = async (organizationId: string) => {
    setLoading('short');
    try {
      const userGroups = (await assetAdminUserGroupClient.adminUserGroupList(organizationId, 9999)).data;

      if (userGroups.contents.length) {
        // Include users in groups to know how many is in each group
        const organization = (await assetAdminOrganizationClient.adminOrganizationRead(organizationId, 'user_groups,user_groups.users')).data;
        const groupsWithUsers: UserGroupWithUsersResponse[] = userGroups.contents.map(userGroup => ({
          ...userGroup,
          users: organization.user_groups?.find(orgGroup => orgGroup.id === userGroup.id)?.users ?? [],
        }));
        setUserGroups(groupsWithUsers);
      }
    } catch (error) {
      console.error(error);
      setApiError(true);
    } finally {
      setLoading('none');
    }
  };

  useEffect(() => {
    fetchUserGroups(organizationId);
  }, [organizationId]);

  const handleSortChange = (sortColumn: SortableColumns) => {
    const sortedUserGroups = [...userGroups];
    if (sortColumn === currentSort) {
      sortedUserGroups.reverse();
    } else {
      if (sortColumn === SortableColumns.Name) {
        sortedUserGroups.sort((a, b) => (a.name && b.name && a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
      }
    }
    setCurrentSort(sortColumn);
    setUserGroups(sortedUserGroups);
    const currentPageUserGroups = sortedUserGroups.slice(0, pageSize);
    handlePageChange(currentPageUserGroups);
  };

  const createUserGroup = async (updateData: Partial<UserGroupRequest>): Promise<boolean> => {
    try {
      const userGroupUpdateRequest: UserGroupRequest = {
        name: updateData.name!,
        default_user_roles: [UserRole.Admin],
      };
      const updatedUserGroup = (await assetAdminUserGroupClient.adminUserGroupCreate(organizationId, userGroupUpdateRequest)).data;
      setUserGroups([
        ...(userGroups || []),
        {
          ...updatedUserGroup,
          users: [],
        },
      ]);
      setIsCreateModalVisible(false);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const clickHandler = (row: UserGroupWithUsersResponse) => {
    history.push(`${withCustomerOrgFormPath(routes.userGroups, organizationId)}/${row.id}`);
  };

  const userGroupCreateTranslations: UserGroupCreateModalTranslations = {
    heading: t('createUserGroupModal.heading'),
    nameInputLabel: t('createUserGroupModal.nameInputLabel'),
    fieldRequired: t('basicInformation.fieldRequired'),
    saveButton: t('basicInformation.saveButton'),
    closeButton: t('basicInformation.closeButton'),
    saveErrorTitle: t('basicInformation.saveErrorTitle'),
    saveErrorMessage: t('basicInformation.saveErrorMessage'),
  };

  return (
    <>
      <Div>
        <Button iconName="add" iconOnRight={true} variant="secondary" onClick={() => setIsCreateModalVisible(true)}>
          {t('UserGroups.createGroupButton')}
        </Button>
        <GenericTable
          loading={loading}
          errorText={apiError ? t('UserGroups.apiError') : undefined}
          noDataText={t('UserGroups.noUserGroupsMessage')}
          inputData={userGroupsPage}
          rowConfigs={[
            {
              header: t('UserGroups.userGroupsTable.nameHeader'),
              key: 'name',
              fullyExternalSortFunction: () => handleSortChange(SortableColumns.Name),
            },
            {
              header: t('UserGroups.userGroupsTable.usersHeader'),
              formatValue: (_keyValue, row) => `${row.users?.length} ${t('UserGroups.userGroupsTable.usersHeader').toLocaleLowerCase()}`,
              hideAtBreakpoint: 'xs',
            },
          ]}
          buttonConfig={{
            name: t('UserGroups.userGroupsTable.buttonText'),
            buttonClickCallback: row => clickHandler(row),
            buttonProps: {
              iconOnRight: true,
              iconName: 'chevron-right',
              variant: 'secondary',
            },
          }}
        />
        <CustomPaginator
          activateInputData={{
            inputData: userGroups,
            pageSize,
          }}
          onPageChange={pageChangeInfo => handlePageChange(pageChangeInfo.nextPageItems)}
        />
      </Div>
      <UserGroupCreateModal
        translations={userGroupCreateTranslations}
        onUserGroupSave={createUserGroup}
        onCloseModal={() => setIsCreateModalVisible(false)}
        countries={COUNTRIES}
        hidden={!isCreateModalVisible}
      />
    </>
  );
};

export default UserGroups;
